import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaUserClock, FaSave, FaClipboardList, FaSearch, FaDownload, FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";
import * as XLSX from "xlsx";

export default function EmployeeAttendance() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [search, setSearch] = useState("");
  const [excelPreviewData, setExcelPreviewData] = useState([]);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const { token } = useAuth();

  const downloadTemplate = () => {
    if (employees.length === 0) {
      toast.warning("No employee data available to generate template.");
      return;
    }

    const sheetData = employees.map((emp) => ({
      "Employee Code": emp.code,
      "Employee Name": emp.name,
      "Status (Present/Absent/Late/HalfDay/Leave)": emp.status || "Present",
      "Time In (HH:MM)": emp.inTime || "09:00",
      "Time Out (HH:MM)": emp.outTime || "17:00",
      "Remarks": emp.remarks || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    
    // Auto-fit columns
    const max_len = sheetData.reduce((acc, row) => {
      Object.keys(row).forEach((key, col_idx) => {
        const val = String(row[key] || "");
        acc[col_idx] = Math.max(acc[col_idx] || 0, val.length, key.length);
      });
      return acc;
    }, []);
    worksheet["!cols"] = max_len.map((len) => ({ wch: len + 3 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Template");

    XLSX.writeFile(workbook, `Employee_Attendance_Template_${selectedDate}.xlsx`);
    toast.success("Template downloaded successfully!");
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        if (data.length === 0) {
          toast.error("The uploaded Excel file is empty.");
          return;
        }

        const firstRow = data[0];
        if (!("Employee Code" in firstRow) || !("Status (Present/Absent/Late/HalfDay/Leave)" in firstRow)) {
          toast.error("Invalid template format. Must contain 'Employee Code' and 'Status (...)' columns.");
          return;
        }

        let matchCount = 0;
        const updatedEmployees = employees.map((emp) => {
          const matchedRow = data.find(
            (row) => String(row["Employee Code"]).trim().toLowerCase() === emp.code.trim().toLowerCase()
          );

          if (matchedRow) {
            matchCount++;
            let status = String(matchedRow["Status (Present/Absent/Late/HalfDay/Leave)"] || "Present").trim();
            if (status.toLowerCase() === "on leave") status = "Leave";
            if (status.toLowerCase() === "half day") status = "HalfDay";

            return {
              ...emp,
              status: ["Present", "Absent", "Late", "HalfDay", "Leave"].includes(status) ? status : "Present",
              inTime: matchedRow["Time In (HH:MM)"] ? String(matchedRow["Time In (HH:MM)"]).trim() : emp.inTime,
              outTime: matchedRow["Time Out (HH:MM)"] ? String(matchedRow["Time Out (HH:MM)"]).trim() : emp.outTime,
              remarks: matchedRow["Remarks"] ? String(matchedRow["Remarks"]).trim() : emp.remarks,
            };
          }
          return emp;
        });

        setEmployees(updatedEmployees);
        setExcelPreviewData(data);
        setUploadedFileName(file.name);
        toast.success(`Imported attendance for ${matchCount} employees from Excel! Click "Save Attendance" to submit.`);
      } catch (err) {
        toast.error("Error reading Excel file.");
        console.error(err);
      }
    };
    e.target.value = "";
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${server}/employee-attendance`, {
        params: { date: selectedDate },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const mappedData = res.data.data.map((item) => ({
        employeeId: item.employee.id,
        name: item.employee.name,
        code: item.employee.employeeCode,
        department:
          typeof item.employee?.department === "object" && item.employee?.department !== null
            ? item.employee.department.name
            : (item.employee?.departmentName || item.employee?.department || "N/A"),
        status: item.attendance?.status || "Present",
        inTime: item.attendance?.inTime
          ? new Date(item.attendance.inTime).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
        outTime: item.attendance?.outTime
          ? new Date(item.attendance.outTime).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
        remarks: item.attendance?.remarks || "",
      }));
      setEmployees(mappedData);
    } catch (error) {
      toast.error("Failed to load attendance data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate && token) {
      fetchAttendance();
      setExcelPreviewData([]);
      setUploadedFileName("");
    }
  }, [selectedDate, token]);

  const handleStatusChange = (id, newStatus) => {
    setEmployees((prev) =>
      prev.map((emp) =>
        emp.employeeId === id ? { ...emp, status: newStatus } : emp,
      ),
    );
  };

  const handleInputChange = (id, field, value) => {
    setEmployees((prev) =>
      prev.map((emp) =>
        emp.employeeId === id ? { ...emp, [field]: value } : emp,
      ),
    );
  };

  const saveAttendance = async () => {
    try {
      const payload = employees.map((emp) => {
        let inTimeISO = null;
        let outTimeISO = null;

        if (emp.inTime) {
          inTimeISO = new Date(`${selectedDate}T${emp.inTime}`).toISOString();
        }
        if (emp.outTime) {
          outTimeISO = new Date(`${selectedDate}T${emp.outTime}`).toISOString();
        }

        return {
          EmployeeId: emp.employeeId,
          Date: selectedDate,
          Status: emp.status,
          InTime: inTimeISO,
          OutTime: outTimeISO,
          Remarks: emp.remarks,
        };
      });

      await axios.post(`${server}/employee-attendance`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Attendance saved successfully!");
      setExcelPreviewData([]);
      setUploadedFileName("");
      fetchAttendance();
    } catch (error) {
      toast.error("Error saving attendance");
      console.error(error);
    }
  };

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name.toLowerCase().includes(search.toLowerCase()) ||
      emp.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaUserClock size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Daily Staff Attendance</h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Mark, verify, and bulk upload daily teaching faculty and staff attendance</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={saveAttendance}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
          >
            <FaSave size={11} /> Save Attendance
          </button>
        </div>
      </div>

      {/* Excel Operations Row */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800 shadow-sm">
        <div className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2">
          <span>Excel Operations:</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Download Template */}
          <button
            onClick={downloadTemplate}
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 text-xs font-bold px-4 py-2 border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 rounded-xl flex items-center gap-2 transition"
          >
            <FaDownload /> Download Template
          </button>
          
          {/* Upload Excel */}
          <label className="text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-xs font-bold px-4 py-2 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl flex items-center gap-2 cursor-pointer transition">
            <FaUpload /> Upload Excel File
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleExcelUpload}
              className="hidden"
            />
          </label>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-lg overflow-hidden border border-slate-200/80 dark:border-slate-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200/80 dark:divide-slate-800">
            <thead className="bg-slate-50 dark:bg-slate-800/80">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Time In
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Time Out
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-200/80 dark:divide-slate-800 text-sm">
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-12">
                    <div className="flex justify-center items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-indigo-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span className="ml-3 text-slate-500 dark:text-slate-400 font-medium">
                        Loading attendance data...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-slate-400 dark:text-slate-500">
                    No employees found
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((emp) => (
                  <tr
                    key={emp.employeeId}
                    className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-bold text-slate-900 dark:text-white">{emp.name}</div>
                      <div className="text-xs text-indigo-600 dark:text-indigo-400 font-mono font-bold">
                        {emp.code}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {typeof emp.department === "object" ? (emp.department?.name || "N/A") : (emp.department || "N/A")}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={emp.status}
                        onChange={(e) =>
                          handleStatusChange(emp.employeeId, e.target.value)
                        }
                        className={`block w-full pl-3 pr-8 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold rounded-xl transition-all
                           ${
                             emp.status === "Present"
                               ? "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/50"
                               : emp.status === "Absent"
                                 ? "bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/50"
                                 : emp.status === "Leave"
                                   ? "bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700/50"
                                   : "bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700/50"
                           }
                        `}
                      >
                        <option value="Present" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Present</option>
                        <option value="Absent" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Absent</option>
                        <option value="Late" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Late</option>
                        <option value="HalfDay" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Half Day</option>
                        <option value="Leave" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">On Leave</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="time"
                        value={emp.inTime}
                        onChange={(e) =>
                          handleInputChange(
                            emp.employeeId,
                            "inTime",
                            e.target.value,
                          )
                        }
                        disabled={
                          emp.status === "Absent" || emp.status === "Leave"
                        }
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-xs font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl p-2.5 disabled:bg-slate-100 dark:disabled:bg-slate-800/40 disabled:text-slate-400 dark:disabled:text-slate-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="time"
                        value={emp.outTime}
                        onChange={(e) =>
                          handleInputChange(
                            emp.employeeId,
                            "outTime",
                            e.target.value,
                          )
                        }
                        disabled={
                          emp.status === "Absent" || emp.status === "Leave"
                        }
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-xs font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl p-2.5 disabled:bg-slate-100 dark:disabled:bg-slate-800/40 disabled:text-slate-400 dark:disabled:text-slate-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        value={emp.remarks}
                        onChange={(e) =>
                          handleInputChange(
                            emp.employeeId,
                            "remarks",
                            e.target.value,
                          )
                        }
                        placeholder="Optional remarks"
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full text-xs font-medium border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 rounded-xl p-2.5"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Received Excel Preview Card */}
      {excelPreviewData.length > 0 && (
        <div className="mt-8 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200/80 dark:border-slate-800 overflow-hidden">
          <div className="bg-slate-50 dark:bg-slate-800/80 px-6 py-4 border-b border-slate-200/80 dark:border-slate-800 flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <FaClipboardList className="text-slate-400" />
              Received Excel Preview: <span className="text-indigo-600 dark:text-indigo-400 font-mono text-sm">{uploadedFileName}</span>
            </h3>
            <button
              onClick={() => {
                setExcelPreviewData([]);
                setUploadedFileName("");
              }}
              className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 font-bold px-3 py-1.5 border border-red-200 dark:border-red-500/30 hover:border-red-300 bg-red-50 dark:bg-red-950/40 rounded-xl transition"
            >
              Clear Preview
            </button>
          </div>
          
          <div className="overflow-x-auto max-h-[300px]">
            <table className="min-w-full divide-y divide-slate-200/80 dark:divide-slate-800 text-xs">
              <thead className="bg-slate-50/50 dark:bg-slate-800/60 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                    Employee Code
                  </th>
                  <th className="px-6 py-3 text-left font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                    Employee Name
                  </th>
                  <th className="px-6 py-3 text-left font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                    Status (Excel)
                  </th>
                  <th className="px-6 py-3 text-left font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                    Time In (Excel)
                  </th>
                  <th className="px-6 py-3 text-left font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                    Time Out (Excel)
                  </th>
                  <th className="px-6 py-3 text-left font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                    Remarks (Excel)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-200/80 dark:divide-slate-800 text-slate-700 dark:text-slate-200">
                {excelPreviewData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="px-6 py-3 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                      {row["Employee Code"] || "N/A"}
                    </td>
                    <td className="px-6 py-3 font-medium">
                      {row["Employee Name"] || "N/A"}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full font-bold text-[11px] ${
                        String(row["Status (Present/Absent/Late/HalfDay/Leave)"]).toLowerCase() === "present"
                          ? "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700/50"
                          : String(row["Status (Present/Absent/Late/HalfDay/Leave)"]).toLowerCase() === "absent"
                            ? "bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-700/50"
                            : "bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700/50"
                      }`}>
                        {row["Status (Present/Absent/Late/HalfDay/Leave)"] || "Present"}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      {row["Time In (HH:MM)"] || "N/A"}
                    </td>
                    <td className="px-6 py-3">
                      {row["Time Out (HH:MM)"] || "N/A"}
                    </td>
                    <td className="px-6 py-3 italic text-slate-400">
                      {row["Remarks"] || "None"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
